<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import { useIsInPdfMode } from "~/stores/pdfModeStore";

import Editor from "@tinymce/tinymce-vue";

import type { FormElementType, RichTextFormElement } from "~/api";
import {
  type FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<RichTextFormElement>>();

const { useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.RICH_TEXT>(props.modelValue);
const localAnswer = ref(answer.value);
const rerender = ref(0);

const toolbarOptions = computed<string | null | undefined>(() =>
  props.modelValue.properties.toolbarElements?.toString().replace(/,/g, " "),
);

const pluginOptions = computed<string | null | undefined>(() =>
  props.modelValue.properties.pluginOptions?.toString().replace(/,/g, " "),
);

const tinyMCEOptions = computed(() =>
  props.isReadOnly
    ? {
        promotion: false,
        license_key: "gpl",
        plugins: `${pluginOptions?.value}`,
        toolbar: false,
        toolbar_mode: "wrap",
        menubar: false,
        statusbar: false,
        max_height: 100,
        inline: true,
        readonly: 1,
        content_style: "body { font-size: 1.3rem; color: #536e82; }", // can put css here for iFrame styling
      }
    : {
        promotion: false,
        license_key: "gpl",
        plugins: `${pluginOptions?.value}`,
        toolbar: `${toolbarOptions?.value}`,
        toolbar_mode: "wrap",
        menubar: false,
        statusbar: false,
        resize: true,
        content_style: "body { font-size: 1.3rem; color: #536e82; }", // can put css here for iFrame styling
      },
);

watch(
  () => tinyMCEOptions.value,
  () => rerender.value++,
);

const getIsValidInput = (input?: string): boolean => {
  // Count these as valid to allow deleting answers
  if (input === undefined || input === null || input === "") {
    return true;
  }

  if (wordCountStatus.value === "error") {
    return false;
  }

  return true;
};

const handleUpdateAnswer = (newVal: string) => {
  // Always save the answer to allow form-level validation to catch errors
  // This ensures questions with validation errors are marked as "invalid" not "unanswered"
  answer.value = newVal;
};

const updateAnswer = () => {
  handleUpdateAnswer(localAnswer.value ?? "");
};

const inPdf = useIsInPdfMode();

const countWords = (text: string) => {
  if (!text || text.trim() === "") {
    return 0;
  }

  const noHtml = text.replace(/<[^>]*>/g, "");
  const decodedText = noHtml.replace(/&[a-zA-Z0-9#]+;/g, "");
  const words = decodedText.match(/\b\w+([-'\u2018\u2019]\w+)*\b/g); // include actual words

  return words ? words.length : 0;
};
const currentWordCount = computed(() => countWords(localAnswer.value ?? ""));

const wordCountDisplay = computed(() => {
  if (!props.modelValue.properties.maxWordCount) {
    return null;
  }
  const current = currentWordCount.value;
  const isEmpty = !localAnswer.value || localAnswer.value.trim() === ""; // if no words entered yet
  if (isEmpty) {
    return `Maximum ${props.modelValue.properties.maxWordCount} words`;
  } else {
    return `Maximum ${props.modelValue.properties.maxWordCount} words - ${current} entered`;
  }
});

const wordCountStatus = computed(() => {
  const maxWords = props.modelValue.properties.maxWordCount;
  if (!maxWords) {
    return "valid";
  }
  const current = currentWordCount.value;
  const warningThreshold = Math.ceil(maxWords * 0.9); // 10% words to go, rounded up
  if (current > maxWords) {
    return "error";
  }
  if (current >= warningThreshold) {
    return "warning";
  }
  return "valid";
});
</script>

<template id="rich-text-component">
  <div class="RichTextComponent">
    <slot name="label" :element="modelValue" />
    <span v-if="!getIsValidInput(localAnswer)" class="invalid-input">
      Invalid Input
    </span>
    <div :key="rerender">
      <Editor
        v-if="!inPdf"
        v-model="localAnswer"
        :init="tinyMCEOptions"
        license-key="gpl"
        :disabled="isReadOnly"
        @input="updateAnswer"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div v-else class="fake-mce-content-body" v-html="localAnswer"></div>
    </div>
    <div
      v-if="wordCountDisplay"
      class="word-count-display"
      :class="{
        'word-count-display-warning': wordCountStatus === 'warning',
        'word-count-display-error': wordCountStatus === 'error',
      }"
    >
      {{ wordCountDisplay }}
    </div>
  </div>
</template>

<style lang="less">
.RichTextComponent {
  .rich-text-element {
    gap: 10px;
  }

  .mce-content-body {
    background: white;
    font-size: 1.3rem;
    color: #536e82;
    padding: 1em;
    border-radius: 5px;
    &.mce-content-readonly {
      box-shadow: 0 0 0 1px var(--el-disabled-border-color) inset;
      background-color: var(--el-color-info-light-9);
    }
  }

  .invalid-input {
    color: var(--el-color-error);
    font-size: 0.9em;
  }
}

.word-count-display {
  font-size: 0.75em;
  color: #666;
  margin-top: 2px;
  margin-left: auto;
  align-self: flex-end;
  &-warning {
    color: #e6a23c;
  }
  &-error {
    color: var(--el-color-error);
  }
}

.tox.tox-silver-sink.tox-tinymce-aux {
  z-index: 2015; // Fix for the dropdown menu not showing up
}
</style>
